'use strict';
/**
 * This is an enhancement of the $http service. It does not support shorthand methods like get(), post(), etc.
 * It takes a config object like $http (which is passed on to $http) that may include extra settings.
 * If passed a loadingIndicator object it will also increase/decrease a reference count and loading state.
 * This indicator object can then be used in the view e.g. to display a loading indicator.
 * The default is that this service will display error messages received from the backend, if any.
 * It can also be configured with a specific error message to be displayed on errors or
 * to disable the error dialog from this service and the caller will then have to deal with
 * the error when the response promise is rejected.
 *
 * This is the simple use:
 *  $enhancedHttp({  method: 'GET',
 *                   headers : {'Content-Type':'application/json'},
 *                   url: '/some/endpoint/' })
 *                   .then(function(data) { var responseData = data } );
 *
 * This will make the request and when the promise is resolved (http request successful) the data
 * is returned in the success function. On errors the $enhancedHttp will display an error dialog.
 *
 * The backend should respond e.g. with a Server Error (status 500) and the error message in the reponse.
 * Either as raw text or preferably in a JSON object of the following format:
 *
 *   {"error" : {"responseCode": "someEndpoint123", "response" : "Wrong input format in parameter %1", "parameters" : ["email"]} }
 *
 * This format will allow for using the responseCode to string lookup in a locale dictionary and expanding with given parameters.
 * If no responseCode the given error text can be used or if the caller is handling the error the caller may use the parameters
 * to expand the caller's error message. Parameters can be omitted if no parameters needed.
 *
 * Here is a more elaborate example:
 *
 * $enhancedHttp({  method: 'GET',
 *                  headers : {'Content-Type':'application/json'},
 *                  url: '/some/endpoint/',
 *                  errorMessage: 'Failed getting some data from some endpoint',
 *                  disableErrorDialog: true,
 *                  retryBtn : true
 *               }, loadingIndicator ).then(function(data), function(error) { log(error.response)});
 *
 * This will disable the service from showing a dialog on errors and the caller will just log the error message.
 * A loading indicator object is passed to the service and loadingIndicator.state is true while waiting for reponse
 * and false when the promise is resolved or rejected.
 *
 * TODO: Provide examples of chaining - and unit tests
 * http://blog.ninja-squad.com/2015/05/28/angularjs-promises/
 * http://blog.ninja-squad.com/2015/06/04/angularjs-promises-2/
 */
angular.module('autoremind.services')
    .factory('$enhancedHttp', [
    '$http', '$q', 'dialogs',
    function ($http, $q, dialogs) {
        var increaseLoadingIndicator = function (loadingIndicator) {
            if (loadingIndicator !== undefined) {
                loadingIndicator.refCount++;
                loadingIndicator.state = loadingIndicator.refCount > 0;
            }
            // NOTE: For debugging purposes we do NOT check for uninitialized object (e.g. state missing)
            //       This way we'll see such errors in the console (while refactoring code to use this service)
        };
        var decreaseLoadingIndicator = function (loadingIndicator) {
            if (loadingIndicator !== undefined) {
                loadingIndicator.refCount--;
                loadingIndicator.state = loadingIndicator.refCount > 0;
            }
        };
        // Return main service function
        var enhancedHttp = function (config, loadingIndicator) {
            increaseLoadingIndicator(loadingIndicator);
            return $http(config)
                .then(function (response) {
                decreaseLoadingIndicator(loadingIndicator);
                return response.data; // Return new resolved promise of response data
            }, 
            // on error
            function (response) {
                var deferred = $q.defer();
                var httpResponse = response.data;
                if (config.returnHttpResponseOnFail) {
                    httpResponse = response;
                }
                if (response.xhrStatus === 'abort') {
                    deferred.resolve(httpResponse);
                }
                else {
                    decreaseLoadingIndicator(loadingIndicator);
                }
                if (response.xhrStatus !== 'abort' &&
                    (config.disableErrorDialog === undefined || config.disableErrorDialog === false)) {
                    if (config.handleStatus !== response.status // TODO: Extend to allow setting a list of status codes
                        && response.status !== 444) {
                        var errorMessage = 'Failed getting data from server [' + config.url + ']';
                        var dialogHeader = 'Error';
                        if (typeof config.errorMessage !== 'undefined') {
                            errorMessage = config.errorMessage;
                            //if (response.data && response.data.error && typeof response.data.error.reason !== 'undefined') {
                        }
                        if (angular.isDefined(response.data) && response.data !== null && angular.isDefined(response.data.error) && angular.isDefined(response.data.error.reason)) {
                            errorMessage += ' (' + response.data.error.reason + ')';
                        }
                        if (response.statusText !== undefined && response.statusText !== '') {
                            dialogHeader = response.statusText;
                        }
                        // TODO: Extend config to also include dialogHeader
                        console.log('there is an error in enhancedHttp');
                        if (typeof config.retryBtn !== 'undefined' && config.retryBtn) {
                            var dialog = dialogs.create('app/common/error-dialog-with-retry-btn.html', 'enhancedHttpCtrl', { dialogHeader: dialogHeader, errorMessage: errorMessage }, {});
                            dialog.result.then(function () {
                                // Retry request
                                enhancedHttp(config, loadingIndicator)
                                    .then(function (data) {
                                    deferred.resolve(httpResponse);
                                }, function (error) {
                                    deferred.reject(httpResponse); // This will cause parent caller to display retry dialog
                                });
                            }, function () {
                                // Close button pressed
                                deferred.reject(httpResponse);
                            });
                        }
                        else {
                            var msg = dialogs.error(dialogHeader, errorMessage);
                            msg.result.then(function () {
                                deferred.reject(httpResponse);
                            }, function () {
                                deferred.reject(httpResponse);
                            });
                        }
                    }
                    else {
                        return $q.reject(httpResponse);
                    }
                }
                else {
                    // TODO: Add support for a config.returnHttpStatus and/or config.returnHttpResponse to return that in stead of just data (in case caller wish to handle specific HTTP status
                    // Create a new rejected promise (otherwise returning response will just resolve the promise)
                    return $q.reject(httpResponse);
                }
                return deferred.promise;
            });
        }; // end of main service function being returned
        return enhancedHttp;
    }
])
    .controller('enhancedHttpCtrl', ['$scope', '$uibModalInstance', 'data', function ($scope, $uibModalInstance, data) {
        $scope.dialogInfo = data;
        $scope.close = function () {
            $uibModalInstance.dismiss('Canceled');
        };
        $scope.retry = function () {
            $uibModalInstance.close();
        };
        $scope.$on('event:auth-logout', function () {
            $uibModalInstance.dismiss('Canceled');
        });
    }]);
//# sourceMappingURL=enhanced-http.js.map